import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Users, TrendingUp, Calendar, Trash2, Mail, Phone,
  Building2, User, Clock, FileText, Loader2, RefreshCw, LogOut, FileDown, CalendarDays
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`} className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <span className="text-neutral-500 text-sm font-medium">{label}</span>
      </div>
      <p className="text-3xl font-black text-white tracking-tight">{value}</p>
    </div>
  );
}

function LeadRow({ lead, onDeleteClick, deleting }) {
  const date = new Date(lead.created_at);
  const formatted = date.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      data-testid={`lead-row-${lead.id}`}
      className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 hover:border-neutral-700 transition-colors"
    >
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-500" />
              <span className="text-white font-bold">{lead.business_name}</span>
            </div>
            <span className="text-neutral-700">|</span>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-neutral-500" />
              <span className="text-neutral-400 text-sm">{lead.contact_name}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 text-neutral-400 hover:text-white transition-colors">
              <Phone className="w-3.5 h-3.5" /> {lead.phone}
            </a>
            <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 text-neutral-400 hover:text-white transition-colors">
              <Mail className="w-3.5 h-3.5" /> {lead.email}
            </a>
            <span className="flex items-center gap-1.5 text-neutral-500">
              <Clock className="w-3.5 h-3.5" /> {formatted}
            </span>
          </div>
          {lead.notes && (
            <div className="flex items-start gap-1.5 text-neutral-500 text-sm">
              <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{lead.notes}</span>
            </div>
          )}
        </div>
        <button
          onClick={() => onDeleteClick(lead)}
          disabled={deleting === lead.id}
          data-testid={`delete-lead-${lead.id}`}
          className="text-neutral-600 hover:text-red-400 transition-colors p-2 rounded-lg hover:bg-red-500/10 disabled:opacity-50"
        >
          {deleting === lead.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </button>
      </div>
    </motion.div>
  );
}

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total_leads: 0, this_week: 0, this_month: 0 });
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exportStart, setExportStart] = useState("");
  const [exportEnd, setExportEnd] = useState("");
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/leads/stats`, { headers }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
      console.error("Failed to fetch data", err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [fetchData, navigate]);

  const handleDeleteClick = (lead) => {
    setDeleteTarget(lead);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleting(id);
    setDeleteTarget(null);
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: getAuthHeaders() });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      setStats((prev) => ({ ...prev, total_leads: prev.total_leads - 1 }));
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
      console.error("Delete failed", err);
    } finally {
      setDeleting(null);
    }
  };

  const handleExportCSV = async () => {
    try {
      let url = `${API}/leads/export/csv`;
      const params = new URLSearchParams();
      if (exportStart) params.append("start_date", new Date(exportStart).toISOString());
      if (exportEnd) {
        const end = new Date(exportEnd);
        end.setHours(23, 59, 59, 999);
        params.append("end_date", end.toISOString());
      }
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      let filename = "anywash_leads";
      if (exportStart) filename += `_from_${exportStart}`;
      if (exportEnd) filename += `_to_${exportEnd}`;
      filename += ".csv";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
      console.error("Export failed", err);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch { /* ignore */ }
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Confirm Delete Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-neutral-900 border-neutral-800" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Lead</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              Are you sure you want to delete the lead from{" "}
              <span className="text-white font-semibold">{deleteTarget?.business_name}</span>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel-button"
              className="bg-neutral-800 border-neutral-700 text-white hover:bg-neutral-700"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-button"
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <nav className="bg-neutral-950/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 flex items-center justify-between h-16">
          <button
            onClick={() => navigate("/")}
            data-testid="admin-back-button"
            className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Site
          </button>
          <span className="text-white font-black text-lg tracking-tight">
            AnyWash<span className="text-blue-500">Pass</span>{" "}
            <span className="text-neutral-500 font-normal text-sm">Admin</span>
          </span>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchData}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 text-neutral-400 hover:text-red-400 transition-colors text-sm"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-2">
            Partner Leads
          </h1>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
            <p className="text-neutral-500">Manage incoming partner applications</p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-3.5 h-3.5 text-neutral-500" />
                <input
                  type="date"
                  value={exportStart}
                  onChange={(e) => setExportStart(e.target.value)}
                  data-testid="export-start-date"
                  className="bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-1.5 text-sm text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                  placeholder="From"
                />
                <span className="text-neutral-600 text-sm">to</span>
                <input
                  type="date"
                  value={exportEnd}
                  onChange={(e) => setExportEnd(e.target.value)}
                  data-testid="export-end-date"
                  className="bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-1.5 text-sm text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                  placeholder="To"
                />
              </div>
              <button
                onClick={handleExportCSV}
                data-testid="export-csv-button"
                className="inline-flex items-center gap-2 bg-neutral-800 hover:bg-neutral-700 text-white rounded-full px-4 py-2 transition-all duration-300 border border-neutral-700 font-bold text-xs tracking-wide"
              >
                <FileDown className="w-3.5 h-3.5" /> Export CSV
              </button>
              {(exportStart || exportEnd) && (
                <button
                  onClick={() => { setExportStart(""); setExportEnd(""); }}
                  data-testid="clear-date-filter"
                  className="text-neutral-500 hover:text-white text-xs transition-colors"
                >
                  Clear dates
                </button>
              )}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <StatCard icon={Users} label="Total Leads" value={stats.total_leads} color="bg-blue-600" />
          <StatCard icon={Calendar} label="This Week" value={stats.this_week} color="bg-emerald-600" />
          <StatCard icon={TrendingUp} label="This Month" value={stats.this_month} color="bg-violet-600" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div data-testid="no-leads-message" className="text-center py-20">
            <Users className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
            <p className="text-neutral-500 text-lg">No leads yet</p>
            <p className="text-neutral-600 text-sm">Partner submissions will appear here</p>
          </div>
        ) : (
          <AnimatePresence>
            <div className="space-y-3">
              {leads.map((lead) => (
                <LeadRow key={lead.id} lead={lead} onDeleteClick={handleDeleteClick} deleting={deleting} />
              ))}
            </div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
